import React, { CSSProperties, ReactNode, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { DeepMap } from "react-hook-form";
import {
  Controller,
  FieldError,
  useForm,
  UseFormMethods,
} from "react-hook-form/dist/index.ie11";

import Checkbox from "~/shared/ui/Fields/Checkbox";
import Input from "~/shared/ui/Fields/Input";
import MultiSelect from "~/shared/ui/Fields/MultiSelect";
import DatesPicker from "~/shared/ui/Fields/PdDatePicker";
import Select from "~/shared/ui/Fields/Select";
import TextArea from "~/shared/ui/Fields/TextArea";
import Wrapper from "~/shared/ui/Wrapper";
import { FileLoaderField } from "~/shared/ui/Fields/FileLoaderField";
import { FormField, IFormGenConfig } from "~/widgets/FormGen/model/schema";

import s from "./FormGen.module.less";

interface IFormGenProps<TFormValues extends Record<string, any>> {
  config: IFormGenConfig<TFormValues>;
  defaultValues?: Partial<TFormValues>;
  clearValues?: Partial<TFormValues>;
  onSubmit: (values: TFormValues) => void;
  submitSlot?:
    | ReactNode
    | ((props: { onDefaultReset: () => void }) => React.ReactNode);
  setFormMethods?: (
    form: Omit<UseFormMethods, "control" | "handleSubmit" | "errors">,
  ) => void;
}

export function FormGen<TFormValues extends Record<string, any>>(
  props: IFormGenProps<TFormValues>,
) {
  const {
    config,
    defaultValues,
    clearValues,
    onSubmit,
    submitSlot,
    setFormMethods,
  } = props;

  const resolverSchema = config.schema;
  const { control, handleSubmit, errors, ...otherFormControlAndState } =
    useForm<TFormValues>({
      defaultValues: defaultValues as any,
      resolver: resolverSchema ? yupResolver(resolverSchema) : undefined,
    });

  useEffect(() => {
    if (setFormMethods) setFormMethods(otherFormControlAndState);
  }, []);

  const gridColumns = config.layout?.columns ?? 2;
  const gridGap = config.layout?.gap ?? 16;

  const gridStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
    gap: typeof gridGap === "number" ? `${gridGap}px` : gridGap,
  };

  const renderField = (field: FormField<TFormValues>, index: number) => {
    const {
      type,
      name,
      label,
      span,
      wrapperClassName,
      labelClassName,
      ...rest
    } = field as any;
    const commonStyle: CSSProperties = span
      ? { gridColumn: `span ${Math.min(span, gridColumns)}` }
      : undefined;

    switch (type) {
      case "input":
        return (
          <div key={index} style={commonStyle}>
            <Wrapper.Field
              label={label}
              className={wrapperClassName}
              labelClassName={labelClassName}
            >
              <Input
                control={control}
                name={name}
                rules={(field as any).rules}
                error={(errors[name] as FieldError)?.message}
                {...(field as any).props}
              />
            </Wrapper.Field>
          </div>
        );

      case "file":
        return (
          <div key={index} style={commonStyle}>
            <Wrapper.Field
              label={label}
              className={wrapperClassName}
              labelClassName={labelClassName}
            >
              <FileLoaderField
                name={name}
                control={control}
                error={(errors[name] as FieldError)?.message}
                {...(field as any).props}
              />
            </Wrapper.Field>
          </div>
        );
      case "select":
        return (
          <div key={index} style={commonStyle}>
            <Wrapper.Field
              label={label}
              className={wrapperClassName}
              labelClassName={labelClassName}
            >
              <Select
                control={control}
                name={name}
                newStyle
                error={
                  (
                    (errors[name] as DeepMap<TFormValues, FieldError>)
                      ?.value as FieldError
                  )?.message
                }
                {...(field as any).props}
              />
            </Wrapper.Field>
          </div>
        );
      case "multiselect":
        return (
          <div key={index} style={commonStyle}>
            <Wrapper.Field
              label={label}
              className={wrapperClassName}
              labelClassName={labelClassName}
            >
              <MultiSelect
                control={control}
                name={name}
                error={(errors[name] as FieldError)?.message}
                {...(field as any).props}
              />
            </Wrapper.Field>
          </div>
        );
      case "textarea":
        return (
          <div key={index} style={commonStyle}>
            <Wrapper.Field
              label={label}
              className={wrapperClassName}
              labelClassName={labelClassName}
            >
              <TextArea
                control={control}
                name={name}
                error={(errors[name] as FieldError)?.message}
                {...(field as any).props}
              />
            </Wrapper.Field>
          </div>
        );
      case "date":
        return (
          <div key={index} style={commonStyle}>
            <Wrapper.Field
              label={label}
              className={wrapperClassName}
              labelClassName={labelClassName}
            >
              <DatesPicker
                control={control}
                name={name}
                customClassName={s.date_q}
                error={(errors[name] as FieldError)?.message}
                {...(field as any).props}
              />
            </Wrapper.Field>
          </div>
        );
      case "checkbox":
        return (
          <div key={index} style={commonStyle}>
            <Wrapper.Field
              label={label}
              className={wrapperClassName}
              labelClassName={labelClassName}
            >
              <Controller
                control={control}
                name={name as any}
                defaultValue={false as any}
                render={({ value, onChange }) => (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Checkbox
                      checked={!!value}
                      onCheck={onChange}
                      name={name}
                      error={(errors[name] as FieldError)?.message}
                      {...(field as any).props}
                    />
                  </div>
                )}
              />
            </Wrapper.Field>
          </div>
        );
      default:
        return null;
    }
  };

  const onDefaultReset = () => otherFormControlAndState.reset(clearValues);

  const onSubmitWrapped: any = (values: any) => onSubmit(values as TFormValues);

  return (
    <form className={s.form} onSubmit={handleSubmit(onSubmitWrapped)}>
      <div style={gridStyle}>
        {config.fields.map((f, i) => renderField(f, i))}
      </div>
      <div style={{ marginTop: 16 }}>
        {submitSlot ? (
          typeof submitSlot === "function" ? (
            submitSlot({ onDefaultReset })
          ) : (
            submitSlot
          )
        ) : (
          <button type="submit">
            {config.layout?.submitText ?? "Сохранить"}
          </button>
        )}
      </div>
    </form>
  );
}
